import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    DataServiceError,
    DefaultDataServiceConfig,
    EntityActionFactory,
    EntityOp,
    EntitySelectors,
    EntitySelectorsFactory,
    HttpUrlGenerator
} from '@ngrx/data';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import * as fromShared from '../../shared';
import { IGenericResponse, ResponseStatus } from '../../shared';
import { LoggingService } from '../../shared/services/logging';
import { EfaactDynamicDataService } from '../efaact.dynamic-data-service';
import { BurdenModel } from './burden.model';

export const entityName = 'Burden';
@Injectable({ providedIn: 'root' })
export class BurdenDataService extends EfaactDynamicDataService<BurdenModel> {
    filterItems = ['Status', 'Row_Version', 'updates'];
    selectors: EntitySelectors<BurdenModel>;

    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        log: LoggingService,
        config: DefaultDataServiceConfig,
        store: Store
    ) {
        super(http, httpUrlGenerator, entityName, log, config, store);
        this.selectors = new EntitySelectorsFactory().create<BurdenModel>(
            this.entityName
        );
        this.usePostForQuery = true;
        this.useJSONStringForPost = true;
    }
    protected save(update: Update<BurdenModel>): Observable<BurdenModel> {
        this.log.verbose({
            type: `${this.entityName}DataService(BurdenDataService):save`,
            update
        });
        const item = {
            ...update.changes
        } as BurdenModel;

        return this.http
            .put<IGenericResponse<any>>(this.entitiesUrl, {
                [`${this.entityName}List`]: [item]
            })
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(this.selectors.selectEntities))
                ),
                map(([resp, burdens]) => {
                    //find the burden we're updating from the store
                    const burden = burdens.find(x => x.id === update.id);
                    //if we can't find it, return the update
                    if (!burden) {
                        return update.changes as BurdenModel;
                    }
                    this.log.verbose({
                        type: `${this.entityName}DataService(BurdenDataService):save:resp`,
                        resp,
                        update
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        //handle errors
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(x => !!x).map(
                                        x => ({
                                            ...x,
                                            Status: {
                                                ...x.Status,
                                                MsgEntityID:
                                                    (update.changes.original &&
                                                        update.changes.original
                                                            .id) ||
                                                    update.changes.id
                                            }
                                        })
                                    )
                                })
                            )
                        );
                        // need to do this b/c the change of a checkbox is immediate and there is no cancel to put it back
                        // if something is wrong with the save
                        const af = new EntityActionFactory();
                        // if (update.changes.SyncPayrollItems === true) {
                        const action = af.create(
                            this.entityName,
                            EntityOp.SAVE_UPDATE_ONE_SUCCESS,
                            {
                                id: update.changes.id,
                                changed: true,
                                changes: {
                                    ...resp.Items[0],
                                    id: update.changes.id
                                }
                            }
                        );
                        this.store.dispatch(action);
                        throwError(
                            new DataServiceError(
                                resp.ErrorMessages.reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'PUT', url: this.entitiesUrl }
                            )
                        );
                        throw new DataServiceError(
                            resp.ErrorMessages.reduce(
                                (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                ''
                            ),
                            { method: 'PUT', url: this.entitiesUrl }
                        );
                    } else {
                        // this sets the status to update icon and tooltip
                        const ret = {
                            ...burden,
                            Status: {
                                StatusCode: ResponseStatus.Success,
                                Message: 'Saved'
                            }
                        } as BurdenModel;
                        const resUpd = resp.Items[0];
                        // if no items in the resp.items, just return what was sent to be updated
                        if (!resUpd) {
                            return update.changes as BurdenModel;
                        }
                        //this updates part of the store model with the values retured from the save
                        ret[resUpd.Calcdesc] = {
                            ...ret[resUpd.Calcdesc],
                            [resUpd.Loadingtype]: resUpd.LoadYesNo
                        };

                        // const rowId = `row-${ret.id}`;
                        // const row = document.getElementById(rowId);
                        // row.scrollIntoView({
                        //     behavior: 'smooth',
                        //     block: 'center',
                        //     inline: 'nearest'
                        // });

                        this.log.verbose({
                            type: `${this.entityName}DataService(BurdenDataService):save:return`,
                            ret,
                            update,
                            resp: resp.Items
                        });
                        return ret;
                    }
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }
}
